import { Icon, RadioItem } from "@atoms";
import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import Spinner from "@legacy/core/components/Spinner";
import { RadioGroup } from "@molecules";
import { AccessCheck } from "@particles";
import { useQuery } from "@tanstack/react-query";
import { faIcon } from "@utils/faIcon";
import formatDuration from "@utils/formatDuration";
import dayjs from "dayjs";
import { Component, Fragment } from "react";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import Banner from "../../core/components/Banner";
import PureComponentWrapper from "../../core/components/PureComponentWrapper";
import BasicSelectField from "../../core/fields/BasicSelectField";
import BooleanField from "../../core/fields/BooleanField";
import SwitchField from "../../core/fields/SwitchField";
import EstimateSearchOrCreateSelect, { estimateToEstimateOption } from "../../estimates/inputs/EstimateSearchOrCreateSelect";
import { filterInventoryLocationChoices } from "../../inventory/utils/utils";
import InvoiceSearchOrCreateSelect, { invoiceToInvoiceOption } from "../../invoices/inputs/InvoiceSearchOrCreateSelect";
import JobSearchOrCreateSelect, { jobToJobOption } from "../../jobs/inputs/JobSearchOrCreateSelect";
import PriceBookItemSearchOrCreateSelect, { priceBookItemToPriceBookItemOption } from "../../pricebook/inputs/PriceBookItemSearchOrCreateSelect";
import HorizontalLayout from "../components/HorizontalLayout";
import { fetchPriceBookData } from "../components/LineItemListDisplay";
import BasicDisplayField from "../fields/BasicDisplayField";
import CharField from "../fields/CharField";
import DateField from "../fields/DateField";
import PriceField from "../fields/PriceField";
import QuantityField from "../fields/QuantityField";
import SearchCreateSelectField from "../fields/SearchCreateSelectField";
import TextField from "../fields/TextField";
import { LineItemUnitTypes, PriceBookItemTypes } from "../utils/enums";
import { getIsTaxable, valueIsDefined } from "../utils/utils";

const LINE_ITEM_TYPE_MODE_MAP = {
    "ADD_SERVICE_CHARGE": PriceBookItemTypes.service,
    "EDIT_SERVICE_CHARGE": PriceBookItemTypes.service,
    "ADD_PART": PriceBookItemTypes.part,
    "EDIT_PART": PriceBookItemTypes.part,
    "ADD_OTHER_CHARGE": PriceBookItemTypes.other,
    "EDIT_OTHER_CHARGE": PriceBookItemTypes.other,
    "ADD_DISCOUNT": PriceBookItemTypes.discount,
    "EDIT_DISCOUNT": PriceBookItemTypes.discount,
}

const SUMMARY_PLACEHOLDERS = {
    [PriceBookItemTypes.service]: "Summary specific to this Service Charge. e.g. 'Replaced all filters.' or 'Cleaned evaporator coils/condensers.'",
    [PriceBookItemTypes.part]: "Summary specific to this Part or Material. e.g. 'R134 Freon.' or 'Turbo Air - 30200R0100 - Air Compressor.' ",
    [PriceBookItemTypes.other]: "Summary specific to this Misc. Charge. e.g. '30+ mile trip/gas charge' or 'Standard diagnostic fee for new customers.'",
    [PriceBookItemTypes.discount]: "Summary specific to this Discount. e.g. 'Thank you for your service, we hope you enjoy this Veteran's Discount.'",
}

// Connected-store modifier group types whose options read best as a set of radio buttons rather than a
// dropdown. Every other choice-style type (a group that carries selectable options) falls back to a dropdown.
const CATALOG_MODIFIER_RADIO_TYPES = ["rectangles", "radio_buttons"]

// A configurator product can carry thousands of option groups; only the first this many are rendered as
// inputs (matching the catalog item details page) so the picker never mounts thousands of fields at once.
// Required groups past the cap are still rendered, since a required group that cannot be filled would
// block the line from ever saving.
const CATALOG_MODIFIER_GROUP_DISPLAY_CAP = 50

function PriceBookPartDataDisplay(props) {
    const { lineItem, errors } = props
    const currentUser = window.CURRENT_USER

    const {
        data: pricebookItem,
        error,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["pricebook-retrieve-by-description", currentUser.id, lineItem.description],
        queryFn: () => fetchPriceBookData(currentUser, lineItem.description),
        staleTime: 60000,
    })

    if (isError) {
        console.error(error)
    } else if (pricebookItem || lineItem.description) {
        return (
            <Fragment>
                <HorizontalLayout>
                    <BasicDisplayField
                        fieldName="manufacturer"
                        fieldLabel="Manufacturer"
                        fieldValue={pricebookItem?.manufacturer || "\u2014"}
                    />
                    <BasicDisplayField
                        fieldName="part_number"
                        fieldLabel="Manufacturer Part #"
                        fieldValue={pricebookItem?.part_number || "\u2014"}
                    />
                </HorizontalLayout>
            </Fragment>

        )
    }
}


class LineItemForm extends Component {

    constructor(props) {
        super(props)

        this.state = {
            selectedPriceBookItem: undefined,
            selectedEstimates: undefined,
            selectedJobs: undefined,
            selectedInvoices: undefined,
            selectedSubtype: undefined,
            subtypeChoices: [],
            ecommerceVendors: undefined,
            selectedItemCatalogLinks: [],
            inventoryLevels: null,
            lastPurchasedDate: undefined,
            catalogItemBasePrice: null,
            catalogModifierGroups: [],
            catalogModifierSelections: {},
            catalogModifierErrors: {},
            catalogModifierLoading: false,
            catalogModifierError: null,
        }
    }

    componentDidMount = async () => {
        const { lineItem, onFormDataChange } = this.props

        let isInventory = false
        let partID = null

        // Fetch subtype choices based on the line item type
        if (lineItem.line_item_type) {
            await this.fetchSubtypeChoices(lineItem.line_item_type);
        }

        if (valueIsDefined(lineItem.description)) {
            // Attempt to find a matching item in the PriceBook
            let params = new URLSearchParams()
            params.append("confirmed", true)
            params.append("description", lineItem.description)

            const priceBookEndpoint = DjangoUrls["pricebook:api-pricebookitem-lightweight-list"](window.MARKETPLACE_ENTITY_SLUG) + `?${params}`
            const priceBookResponse = await fetch(priceBookEndpoint)
            const priceBookResponseJSON = await priceBookResponse.json()

            if (priceBookResponseJSON.results.length !== 0) {
                const priceBookItem = priceBookResponseJSON.results[0]

                isInventory = this.itemIsInventoryPart(priceBookItem)
                partID = priceBookItem.id

                this.setState({selectedPriceBookItem: priceBookItem})

                // The lightweight match omits catalog links, so the connected-catalog awareness the
                // e-commerce vendor field gates on is pulled from the item detail for sales-facing part lines.
                if (this.isSalesLineItem() && lineItem.line_item_type === PriceBookItemTypes.part) {
                    this.fetchSelectedItemCatalogLinks(partID)
                }

                // If no subtype is set in the line item, use the subtype from the pricebook item (if available)
                if (valueIsDefined(priceBookItem.subtype) && lineItem.subtype === undefined) {
                    const selectedSubtype = this.state.subtypeChoices.find(subtype => subtype.value === priceBookItem.subtype);
                    onFormDataChange("subtype", selectedSubtype.value);
                    onFormDataChange("subtype_label", selectedSubtype.label);
                    this.setState({ selectedSubtype });
                }
            }
            else {
                this.setState({selectedPriceBookItem: null})
            }
        }
        else {
            this.setState({selectedPriceBookItem: null})
        }

        const hasEstimatesEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_estimates_enabled;
        if (hasEstimatesEntitlement && valueIsDefined(lineItem.estimates) && lineItem.estimates.length !== 0) {
            const endpoint = DjangoUrls["estimates:api-estimates-lightweight-list"](window.MARKETPLACE_ENTITY_SLUG)
            const searchParams = new URLSearchParams()
            searchParams.set("id", lineItem.estimates.join(","))

            let selectedEstimates = []

            let selectedEstimatesEndpoint
            let selectedEstimatesResponse
            let selectedEstimatesResponseJSON = {
                next: endpoint + "?" + searchParams.toString(),
                previous: null,
                results: [],
            }

            while (selectedEstimatesResponseJSON.next !== null) {
                selectedEstimatesEndpoint = selectedEstimatesResponseJSON.next
                selectedEstimatesResponse = await fetch(selectedEstimatesEndpoint)
                selectedEstimatesResponseJSON = await selectedEstimatesResponse.json()
                selectedEstimates.push(...selectedEstimatesResponseJSON.results)
            }

            this.setState({selectedEstimates: selectedEstimates})
        }
        else {
            this.setState({selectedEstimates: []})
        }

        const hasJobsEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_jobs_enabled;
        if (hasJobsEntitlement && valueIsDefined(lineItem.jobs) && lineItem.jobs.length !== 0) {
            const endpoint = DjangoUrls["jobs:api-jobs-lightweight-list"](window.MARKETPLACE_ENTITY_SLUG)
            const searchParams = new URLSearchParams()
            searchParams.set("id", lineItem.jobs.join(","))

            let selectedJobs = []

            let selectedJobsEndpoint
            let selectedJobsResponse
            let selectedJobsResponseJSON = {
                next: endpoint + "?" + searchParams.toString(),
                previous: null,
                results: [],
            }

            while (selectedJobsResponseJSON.next !== null) {
                selectedJobsEndpoint = selectedJobsResponseJSON.next
                selectedJobsResponse = await fetch(selectedJobsEndpoint)
                selectedJobsResponseJSON = await selectedJobsResponse.json()
                selectedJobs.push(...selectedJobsResponseJSON.results)
            }

            this.setState({selectedJobs: selectedJobs})
        }
        else {
            this.setState({selectedJobs: []})
        }

        const hasInvoicesEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_invoices_enabled;
        if (hasInvoicesEntitlement && valueIsDefined(lineItem.invoices) && lineItem.invoices.length !== 0) {
            const endpoint = DjangoUrls["invoices:api-invoices-lightweight-list"](window.MARKETPLACE_ENTITY_SLUG)
            const searchParams = new URLSearchParams()
            searchParams.set("id", lineItem.invoices.join(","))

            let selectedInvoices = []

            let selectedInvoicesEndpoint
            let selectedInvoicesResponse
            let selectedInvoicesResponseJSON = {
                next: endpoint + "?" + searchParams.toString(),
                previous: null,
                results: [],
            }

            while (selectedInvoicesResponseJSON.next !== null) {
                selectedInvoicesEndpoint = selectedInvoicesResponseJSON.next
                selectedInvoicesResponse = await fetch(selectedInvoicesEndpoint)
                selectedInvoicesResponseJSON = await selectedInvoicesResponse.json()
                selectedInvoices.push(...selectedInvoicesResponseJSON.results)
            }

            this.setState({selectedInvoices: selectedInvoices})
        }
        else {
            this.setState({selectedInvoices: []})
        }

        if (isInventory && partID) {
            this.fetchInventoryLevels(partID)
        }

        if (this.isSalesLineItem()) {
            this.fetchEcommerceVendors()
        }

        // Hydrate the connected-store product options from any selections already saved on this line.
        this.resolveCatalogModifiers(partID, this.getCatalogVendorID(), lineItem.platform_options)

        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
    }

    componentDidUpdate(prevProps, prevState) {
        // If line_item_type changes, fetch new subtype choices
        if (this.props.lineItem.line_item_type !== prevProps.lineItem.line_item_type) {
            if (this.props.lineItem.line_item_type) {
                this.fetchSubtypeChoices(this.props.lineItem.line_item_type);
            } else {
                this.setState({ subtypeChoices: [] });
            }
        }

        // Re-resolve product options whenever the selected part changes. A fresh part starts with no saved
        // selections, so nothing is hydrated and the picker resets to the new item's options. Vendor changes
        // can't be watched here — the containers mutate the shared line item object in place, so prop diffing
        // never sees them — so they trigger resolution at the change site via handleEcommerceVendorChange.
        // The initial mount transition (undefined -> matched item) is skipped: componentDidMount resolves it
        // WITH the line's saved selections, and firing here too would race that hydration with a default-seeding
        // resolve that can overwrite the saved options. An explicit deselection sets null, not undefined, so
        // genuine part changes still re-resolve.
        const currentPartID = this.state.selectedPriceBookItem?.id ?? null
        const previousPartID = prevState.selectedPriceBookItem?.id ?? null
        if (currentPartID !== previousPartID && prevState.selectedPriceBookItem !== undefined) {
            this.resolveCatalogModifiers(currentPartID, this.getCatalogVendorID(), null)
        }
    }

    clearCatalogModifiers = (errorMessage = null) => {
        // Skipped when there is nothing to clear so a no-op resolve never re-renders the form.
        const hasState = this.state.catalogModifierGroups.length !== 0
            || this.state.catalogModifierError !== null
            || this.state.catalogModifierLoading
        if (hasState || errorMessage !== null) {
            this.setState({
                catalogItemBasePrice: null,
                catalogModifierGroups: [],
                catalogModifierSelections: {},
                catalogModifierErrors: {},
                catalogModifierLoading: false,
                catalogModifierError: errorMessage,
            })
        }
    }

    resolveCatalogModifiers = async (pricebookItemID, vendorID, savedPlatformOptions) => {
        // Digital ordering applies to a purchase order buying through a connected catalog and to a sales line
        // sourced from a connected e-commerce vendor, so everything below is skipped unless this is one of those
        // contexts with both a resolved part and vendor.
        if ((!this.props.isPurchaseOrderLineItem && !this.isSalesLineItem()) || !valueIsDefined(pricebookItemID) || !valueIsDefined(vendorID)) {
            this.clearCatalogModifiers()
            return
        }

        this.setState({ catalogModifierLoading: true, catalogModifierError: null })

        try {
            const params = new URLSearchParams()
            params.append("pricebook_item", pricebookItemID)
            params.append("vendor", vendorID)
            const resolveEndpoint = DjangoUrls["catalogs:api-catalogs-links-resolve"](window.MARKETPLACE_ENTITY_SLUG) + `?${params}`
            const resolveResponse = await fetch(resolveEndpoint)

            if (resolveResponse.ok) {
                const resolveBody = await resolveResponse.json()
                await this.fetchCatalogModifiers(resolveBody.id, resolveBody.list_price, savedPlatformOptions)
            } else {
                this.clearCatalogModifiers()
            }
        } catch (error) {
            this.clearCatalogModifiers("Could not load product options.")
        }
    }

    fetchCatalogModifiers = async (catalogItemID, listPrice, savedPlatformOptions) => {
        const endpoint = DjangoUrls["catalogs:api-catalogs-items-modifiers"](window.MARKETPLACE_ENTITY_SLUG, catalogItemID)
        const response = await fetch(endpoint)

        if (!response.ok) {
            throw new Error("Could not load product options.")
        }

        const body = await response.json()
        const groups = body.modifier_groups || []
        const basePrice = valueIsDefined(listPrice) ? listPrice : null
        const selections = this.buildCatalogModifierSelections(groups, savedPlatformOptions)
        this.setState({
            catalogItemBasePrice: basePrice,
            catalogModifierGroups: groups,
            catalogModifierSelections: selections,
            catalogModifierErrors: {},
            catalogModifierLoading: false,
            catalogModifierError: null,
        })
        this.logCatalogPricing(groups, selections, basePrice)

        // A fresh line has no saved selections, so any default the store pre-selects has to be written
        // onto the form here; seeding it into picker state alone would not survive submission unless the
        // user happened to touch the field. A line that already carries saved options is left untouched.
        const hasSavedPlatformOptions = (savedPlatformOptions || []).length > 0
        if (!hasSavedPlatformOptions) {
            this.persistDefaultCatalogModifierSelections(groups, selections, basePrice)
        }
    }

    buildCatalogModifierSelections = (groups, savedPlatformOptions) => {
        const savedByGroup = new Map(
            (savedPlatformOptions || []).map(option => [String(option.modifier_external_id), option]),
        )
        const selections = {}
        groups.forEach(group => {
            const savedOption = savedByGroup.get(String(group.external_id))
            if (savedOption) {
                selections[group.external_id] = String(savedOption.value)
            } else {
                selections[group.external_id] = this.getDefaultCatalogModifierSelection(group)
            }
        })
        return selections
    }

    getDefaultCatalogModifierSelection = (group) => {
        // Only choice-based groups have selectable options that a store can flag as pre-selected; text,
        // date, and number groups have no default option, so they seed empty.
        const defaultOption = this.groupIsChoiceBased(group)
            ? (group.modifiers || []).find(modifier => modifier.is_selected_by_default)
            : undefined

        if (defaultOption) {
            return String(defaultOption.external_id)
        } else {
            return ""
        }
    }

    persistDefaultCatalogModifierSelections = (groups, selections, basePrice) => {
        const hasDefaultSelection = groups.some(group => (selections[group.external_id] || "") !== "")

        if (hasDefaultSelection) {
            const platformOptions = this.buildPlatformOptions(groups, selections)
            this.props.onFormDataChange("platform_options", platformOptions)

            // Mirror handleCatalogModifierChange so the pre-selected defaults are reflected in the cost.
            const adjustedPrice = this.getCatalogAdjustedPrice(basePrice, groups, selections)
            if (adjustedPrice !== null) {
                this.props.onFormDataChange(this.getCatalogCostFieldName(), adjustedPrice.toFixed(2))
            }
        }
    }

    groupIsChoiceBased = (group) => {
        return (group.modifiers || []).length > 0
    }

    handleCatalogModifierChange = (groupExternalID, value) => {
        const { catalogModifierGroups, catalogItemBasePrice } = this.state
        const catalogModifierSelections = {
            ...this.state.catalogModifierSelections,
            [groupExternalID]: value,
        }

        const platformOptions = this.buildPlatformOptions(catalogModifierGroups, catalogModifierSelections)
        this.props.onFormDataChange("platform_options", platformOptions)

        // Surface the modifier-adjusted price as the cost so it is pre-filled but still editable.
        const adjustedPrice = this.getCatalogAdjustedPrice(catalogItemBasePrice, catalogModifierGroups, catalogModifierSelections)
        if (adjustedPrice !== null) {
            this.props.onFormDataChange(this.getCatalogCostFieldName(), adjustedPrice.toFixed(2))
        }
        this.logCatalogPricing(catalogModifierGroups, catalogModifierSelections, catalogItemBasePrice)

        // Clear any required-selection error the moment this group is given a value.
        const catalogModifierErrors = { ...this.state.catalogModifierErrors }
        delete catalogModifierErrors[`catalog_modifier_${groupExternalID}`]

        this.setState({ catalogModifierSelections, catalogModifierErrors })
    }

    catalogModifierIsSatisfied = (group) => {
        const selectedValue = this.state.catalogModifierSelections[group.external_id]
        return selectedValue !== undefined && selectedValue !== null && selectedValue !== ""
    }

    handleRequestAction = (mode) => {
        // The store flags which option groups are mandatory, so a required group left empty blocks the line
        // here rather than being deferred to a rejection from the store. The check is uniform across every
        // group type: an empty string is the unselected state for choices, text, dates, and numbers alike.
        const catalogModifierErrors = {}
        this.state.catalogModifierGroups.forEach(group => {
            if (group.is_required && !this.catalogModifierIsSatisfied(group)) {
                catalogModifierErrors[`catalog_modifier_${group.external_id}`] = "This option is required."
            }
        })

        this.setState({ catalogModifierErrors })

        if (Object.keys(catalogModifierErrors).length === 0) {
            this.props.requestAction(mode)
        }
    }

    buildPlatformOptions = (groups, selections) => {
        const platformOptions = []
        groups.forEach(group => {
            const selectedValue = selections[group.external_id]

            if (selectedValue && this.groupIsChoiceBased(group)) {
                const selectedOption = this.getCatalogSelectedOption(group, selections)
                // A stale saved value that no longer names one of the store's options is dropped.
                if (selectedOption) {
                    platformOptions.push({
                        modifier_external_id: group.external_id,
                        modifier_name: group.name,
                        value: selectedOption.external_id,
                        value_label: selectedOption.name,
                        price_adjustment: selectedOption.price_adjustment_type
                            ? { type: selectedOption.price_adjustment_type, value: selectedOption.price_adjustment_value }
                            : null,
                    })
                }
            } else if (selectedValue) {
                platformOptions.push({
                    modifier_external_id: group.external_id,
                    modifier_name: group.name,
                    value: selectedValue,
                    value_label: selectedValue,
                    price_adjustment: null,
                })
            }
        })
        return platformOptions
    }

    getCatalogSelectedOption = (group, selections) => {
        const selectedValue = selections[group.external_id]

        if (selectedValue && this.groupIsChoiceBased(group)) {
            return (group.modifiers || []).find(modifier => String(modifier.external_id) === String(selectedValue)) || null
        } else {
            return null
        }
    }

    getCatalogAdjustedPrice = (basePrice, groups, selections) => {
        // Each adjuster is applied against the base price, so the result does not depend on group order.
        const parsedBasePrice = parseFloat(basePrice)

        if (Number.isNaN(parsedBasePrice)) {
            return null
        } else {
            let adjustedPrice = parsedBasePrice
            groups.forEach(group => {
                const selectedOption = this.getCatalogSelectedOption(group, selections)
                const adjustmentValue = parseFloat(selectedOption?.price_adjustment_value)

                if (selectedOption?.price_adjustment_type === "relative" && !Number.isNaN(adjustmentValue)) {
                    adjustedPrice += adjustmentValue
                } else if (selectedOption?.price_adjustment_type === "percentage" && !Number.isNaN(adjustmentValue)) {
                    adjustedPrice += parsedBasePrice * (adjustmentValue / 100)
                }
            })
            return adjustedPrice
        }
    }

    describeCatalogAdjuster = (option) => {
        const adjustmentValue = parseFloat(option.price_adjustment_value)
        const sign = adjustmentValue >= 0 ? "+" : ""

        if (!option.price_adjustment_type || Number.isNaN(adjustmentValue)) {
            return ""
        } else if (option.price_adjustment_type === "percentage") {
            return ` (${sign}${adjustmentValue}%)`
        } else {
            return ` (${sign}${adjustmentValue.toFixed(2)})`
        }
    }

    logCatalogPricing = (groups, selections, basePrice) => {
        // The store's pricing math is surfaced on the console only: the form already carries the adjusted
        // price through Expected Vendor Cost, and a visible breakdown would only crowd the picker.
        const formatCurrencyValue = this.props.formatCurrencyValue || (value => `${this.props.currencySymbol || ""}${value.toFixed(2)}`)
        const parsedBasePrice = parseFloat(basePrice)
        const adjustedPrice = this.getCatalogAdjustedPrice(basePrice, groups, selections)
        const optionsApplied = []
        groups.forEach(group => {
            const selectedOption = this.getCatalogSelectedOption(group, selections)
            if (selectedOption) {
                optionsApplied.push(`${group.name}: ${selectedOption.name}${this.describeCatalogAdjuster(selectedOption)}`)
            }
        })
        const formattedBasePrice = Number.isNaN(parsedBasePrice) ? "—" : formatCurrencyValue(parsedBasePrice)
        const formattedAdjustedPrice = adjustedPrice === null ? "—" : formatCurrencyValue(adjustedPrice)
        console.info(`Store product options: base price ${formattedBasePrice}, with options ${formattedAdjustedPrice}`, optionsApplied)
    }

    getVisibleCatalogModifierGroups = () => {
        return this.state.catalogModifierGroups.filter((group, index) => index < CATALOG_MODIFIER_GROUP_DISPLAY_CAP || group.is_required)
    }

    renderCatalogModifierInput = (group) => {
        const { catalogModifierSelections } = this.state
        // Merge the form's own required-selection errors over any the parent supplied so each modifier field
        // renders its message the same way the rest of the form does.
        const errors = { ...this.props.errors, ...this.state.catalogModifierErrors }
        const fieldName = `catalog_modifier_${group.external_id}`
        const selectedValue = catalogModifierSelections[group.external_id] || ""

        if (this.groupIsChoiceBased(group)) {
            const options = (group.modifiers || []).map(modifier => ({
                value: String(modifier.external_id),
                label: modifier.name,
            }))

            if (CATALOG_MODIFIER_RADIO_TYPES.includes(group.modifier_type)) {
                return (
                    <div className="data-panel__form__field" id={`div_id_${fieldName}`} aria-label={group.name} key={fieldName}>
                        <label className="data-panel__form__field__label in-form">
                            {group.name}
                            {!group.is_required && <span className="text-optional"> (optional)</span>}
                        </label>
                        <div className="data-panel__form__field__input">
                            <RadioGroup
                                value={selectedValue}
                                onValueChange={value => this.handleCatalogModifierChange(group.external_id, value || "")}
                            >
                                {options.map(option => (
                                    <RadioItem key={option.value} value={option.value} label={option.label} showLabel={true} />
                                ))}
                            </RadioGroup>
                        </div>
                        {errors[fieldName] && <div className="data-panel__form__field__errors" aria-label="Field Errors">{errors[fieldName]}</div>}
                    </div>
                )
            }

            const choices = [
                { value: "", label: "Choose an option..." },
                ...options,
            ]
            return (
                <BasicSelectField
                    key={fieldName}
                    fieldName={fieldName}
                    fieldLabel={group.name}
                    fieldValue={selectedValue}
                    fieldOnChange={value => this.handleCatalogModifierChange(group.external_id, value || "")}
                    choices={choices}
                    optional={!group.is_required}
                    errors={errors}
                ></BasicSelectField>
            )
        }

        if (group.modifier_type === "date") {
            return (
                <DateField
                    key={fieldName}
                    fieldName={fieldName}
                    fieldLabel={group.name}
                    fieldValue={selectedValue}
                    fieldOnChange={value => this.handleCatalogModifierChange(group.external_id, value || "")}
                    optional={!group.is_required}
                    errors={errors}
                ></DateField>
            )
        }

        if (group.modifier_type === "numbers_only_text") {
            return (
                <QuantityField
                    key={fieldName}
                    fieldName={fieldName}
                    fieldLabel={group.name}
                    fieldValue={selectedValue}
                    fieldOnChange={value => this.handleCatalogModifierChange(group.external_id, value || "")}
                    optional={!group.is_required}
                    errors={errors}
                ></QuantityField>
            )
        }

        if (group.modifier_type === "multi_line_text") {
            return (
                <TextField
                    key={fieldName}
                    fieldName={fieldName}
                    fieldLabel={group.name}
                    fieldValue={selectedValue}
                    fieldOnChange={value => this.handleCatalogModifierChange(group.external_id, value || "")}
                    rows={3}
                    optional={!group.is_required}
                    errors={errors}
                ></TextField>
            )
        }

        return (
            <CharField
                key={fieldName}
                fieldName={fieldName}
                fieldLabel={group.name}
                fieldValue={selectedValue}
                fieldOnChange={value => this.handleCatalogModifierChange(group.external_id, value || "")}
                optional={!group.is_required}
                errors={errors}
            ></CharField>
        )
    }

    renderCatalogModifiers = () => {
        const { catalogModifierGroups, catalogModifierLoading, catalogModifierError } = this.state
        const visibleGroups = this.getVisibleCatalogModifierGroups()
        const hiddenGroupCount = catalogModifierGroups.length - visibleGroups.length

        if (catalogModifierLoading) {
            return <Banner type="info" text="Loading product options..." extraMargin={true} />
        } else if (catalogModifierError) {
            return <Banner type="warning" text={catalogModifierError} extraMargin={true} />
        } else if (catalogModifierGroups.length !== 0) {
            return (
                <fieldset>
                    <legend>Store Product Options</legend>
                    {visibleGroups.map(group => this.renderCatalogModifierInput(group))}
                    {hiddenGroupCount > 0 && (
                        <p className="data-panel__form__caption">Showing {visibleGroups.length} of {catalogModifierGroups.length} option groups.</p>
                    )}
                </fieldset>
            )
        } else {
            return null
        }
    }

    renderSummaryField = () => {
        const { mode, lineItem, onFormDataChange, errors, isJobLineItem = false } = this.props

        return (
            <TextField
                key={`summary_${this.state.selectedPriceBookItem?.id}`}
                fieldName="summary"
                fieldLabel={`${mode.includes("_SERVICE_CHARGE") ? "Service" : "Detailed"} Summary`}
                fieldValue={lineItem.summary || ""}
                fieldOnChange={summary => onFormDataChange("summary", summary || "")}
                placeholder={SUMMARY_PLACEHOLDERS[LINE_ITEM_TYPE_MODE_MAP[mode]]}
                rows={3}
                optional={!(isJobLineItem && mode.includes("_SERVICE_CHARGE"))}
                errors={errors}
            ></TextField>
        )
    }

    itemIsInventoryPart = (priceBookItem) => {
        return valueIsDefined(priceBookItem) && priceBookItem.pricebook_item_type === PriceBookItemTypes.part && priceBookItem.track_inventory === true
    }

    renderInventoryLocationChangedWarning = (lineItem, isCreditNoteLineItem, parentLineItems) => {
        if (
            !isCreditNoteLineItem
            || !valueIsDefined(lineItem.invoice_line_item)
            || lineItem.line_item_type !== PriceBookItemTypes.part
            || !this.itemIsInventoryPart(this.state.selectedPriceBookItem)
        ) {
            return null
        }

        const parentLineItem = parentLineItems.find(item => item.id === lineItem.invoice_line_item)
        if (!parentLineItem || !valueIsDefined(parentLineItem.inventory_location)) {
            return null
        }

        const parentLocationStr = String(parentLineItem.inventory_location)
        const currentLocationStr = valueIsDefined(lineItem.inventory_location) ? String(lineItem.inventory_location) : ""
        if (currentLocationStr === parentLocationStr) {
            return null
        }

        const parentLocationLabel = parentLineItem.inventory_location_label
            || window.INVENTORY_LOCATION_CHOICES?.find(location => location.value === parseInt(parentLineItem.inventory_location))?.label
            || "the original location"

        return (
            <Banner
                type="warning"
                text={`The inventory location on the invoice line item is "${parentLocationLabel}". Changing this may cause inventory to be returned to the wrong location.`}
                extraMargin={true}
            />
        )
    }

    fetchInventoryLevels = async (partID) => {
        const inventoryLevelsEndpoint = DjangoUrls["inventory:api-inventory-levels-list"](window.MARKETPLACE_ENTITY_SLUG, partID)
        const inventoryLevelsResponse = await fetch(inventoryLevelsEndpoint)
        const inventoryLevelsResponseJSON = await inventoryLevelsResponse.json()

        this.setState({inventoryLevels: inventoryLevelsResponseJSON})
    }

    fetchLastPurchasedDate = async (partID, vendorID) => {
        const lastPurchasedDateEndpoint = DjangoUrls["pricebook:api-pricebookitem-last-purchased-detail"](window.MARKETPLACE_ENTITY_SLUG, vendorID, partID)
        const lastPurchasedDateResponse = await fetch(lastPurchasedDateEndpoint)
        const lastPurchasedDateResponseJSON = await lastPurchasedDateResponse.json()

        this.setState({lastPurchasedDate: lastPurchasedDateResponseJSON.last_purchased})
    }

    fetchSubtypeChoices = async (lineItemType) => {
        const endpoint = DjangoUrls["pricebook:api-pricebookitem-subtypes-list"](window.MARKETPLACE_ENTITY_SLUG) + `?pricebook_item_type=${lineItemType}`;
        try {
            const response = await fetch(endpoint);
            const responseJSON = await response.json();
            const subtypeChoices = responseJSON.map(subtype => ({
                value: subtype.id,
                label: subtype.name,
            }));
            this.setState({ subtypeChoices });

            const { lineItem, onFormDataChange } = this.props;

            if (valueIsDefined(lineItem.subtype)) {
                const selectedSubtype = subtypeChoices.find(subtype => subtype.value === lineItem.subtype);
                if (selectedSubtype) {
                    onFormDataChange("subtype", selectedSubtype.value);
                    onFormDataChange("subtype_label", selectedSubtype.label);
                    this.setState({ selectedSubtype });
                }
            } else if (subtypeChoices.length === 1 && window.CURRENT_USER?.service_company?.require_subtypes) {
                const defaultSubtype = subtypeChoices[0];
                onFormDataChange("subtype", defaultSubtype.value);
                onFormDataChange("subtype_label", defaultSubtype.label);
                this.setState({ selectedSubtype: defaultSubtype });
            }
        } catch (error) {
            console.error("Error fetching subtype choices:", error);
            this.setState({ subtypeChoices: [] });
        }
    };

    isSalesLineItem = () => {
        // The e-commerce vendor field is a sales-side sourcing hint, so it is scoped to the estimate, job, and
        // invoice contexts and never the purchase order, bill, or credit note contexts that buy or return parts.
        return !!(this.props.isEstimateLineItem || this.props.isJobLineItem || this.props.isInvoiceLineItem)
    }

    getCatalogVendorID = () => {
        // A purchase order resolves its catalog against the order's vendor, while a sales line resolves against
        // the line's own e-commerce vendor. Bills and credit notes never drive catalog resolution.
        if (this.props.isPurchaseOrderLineItem) {
            return valueIsDefined(this.props.parent.vendor) ? this.props.parent.vendor : null
        } else if (this.isSalesLineItem()) {
            return valueIsDefined(this.props.lineItem.ecommerce_vendor) ? this.props.lineItem.ecommerce_vendor : null
        } else {
            return null
        }
    }

    getCatalogCostFieldName = () => {
        // A purchase order stages the modifier-adjusted price as the expected vendor cost; a sales line stages
        // it as the line's own cost. Either field stays hand-editable after the flow pre-fills it.
        return this.props.isPurchaseOrderLineItem ? "expected_cost" : "cost"
    }

    fetchEcommerceVendors = async () => {
        const endpoint = DjangoUrls["pricebook:api-vendors-lightweight-list"](window.MARKETPLACE_ENTITY_SLUG)
        const searchParams = new URLSearchParams()
        searchParams.set("is_active", true)
        searchParams.set("has_ecommerce_catalog", true)

        let ecommerceVendors = []

        let ecommerceVendorsEndpoint
        let ecommerceVendorsResponse
        let ecommerceVendorsResponseJSON = {
            next: endpoint + "?" + searchParams.toString(),
            previous: null,
            results: [],
        }

        while (ecommerceVendorsResponseJSON.next !== null) {
            ecommerceVendorsEndpoint = ecommerceVendorsResponseJSON.next
            ecommerceVendorsResponse = await fetch(ecommerceVendorsEndpoint)
            ecommerceVendorsResponseJSON = await ecommerceVendorsResponse.json()
            ecommerceVendors.push(...ecommerceVendorsResponseJSON.results)
        }

        this.setState({ecommerceVendors: ecommerceVendors}, this.maybeAutoSelectEcommerceVendor)
    }

    fetchSelectedItemCatalogLinks = async (pricebookItemID) => {
        const detailEndpoint = DjangoUrls["pricebook:api-pricebookitem-detail"](window.MARKETPLACE_ENTITY_SLUG, pricebookItemID)
        const detailResponse = await fetch(detailEndpoint)
        const detailResponseJSON = await detailResponse.json()

        this.setState({selectedItemCatalogLinks: detailResponseJSON.catalog_links || []}, this.maybeAutoSelectEcommerceVendor)
    }

    hasConnectedCatalogLink = () => {
        return (this.state.selectedItemCatalogLinks || []).some(catalogLink => catalogLink.is_connected)
    }

    shouldShowEcommerceVendorSelect = () => {
        return (
            this.isSalesLineItem()
            && this.props.lineItem.line_item_type === PriceBookItemTypes.part
            && this.hasConnectedCatalogLink()
            && (this.state.ecommerceVendors || []).length !== 0
        )
    }

    maybeAutoSelectEcommerceVendor = () => {
        // A part with a single connected e-commerce vendor has no choice to make, so the vendor is filled in the
        // moment the field becomes eligible and is still unset, saving the user a redundant selection.
        if (!this.shouldShowEcommerceVendorSelect() || valueIsDefined(this.props.lineItem.ecommerce_vendor)) {
            return
        }
        if (this.state.ecommerceVendors.length === 1) {
            this.handleEcommerceVendorChange(this.state.ecommerceVendors[0].id)
        }
    }

    handleEcommerceVendorChange = (ecommerceVendorID) => {
        // The containers mutate the shared line item object in place, so a vendor change is invisible to
        // componentDidUpdate's prop diffing — resolution is triggered here, at the moment of the change.
        // Clearing the vendor drops any options staged for it.
        this.props.onFormDataChange("ecommerce_vendor", ecommerceVendorID)
        if (!valueIsDefined(ecommerceVendorID)) {
            this.props.onFormDataChange("platform_options", [])
        }
        this.resolveCatalogModifiers(this.state.selectedPriceBookItem?.id ?? null, ecommerceVendorID, null)
    }

    shouldShowVendorCostWarning = () => {
        if (valueIsDefined(this.state.lastPurchasedDate)) {
            const lastPurchasedDate = dayjs(this.state.lastPurchasedDate)
            const oneMonthAgo = dayjs().subtract(1, "month")

            return lastPurchasedDate.isBefore(oneMonthAgo)
        }
        else {
            return false
        }
    }

    getFormCaption = (mode, objectName) => {
        const FORM_MODE_CAPTIONS = {
            ADD_SERVICE_CHARGE: `Please provide the following information to add a new service charge to this ${objectName.toLowerCase()}:`,
            EDIT_SERVICE_CHARGE: "Use the fields below to edit this service charge",
            ADD_PART: `Please provide the following information to add a new part or material to this ${objectName.toLowerCase()}:`,
            EDIT_PART: "Use the fields below to edit this part or material:",
            ADD_OTHER_CHARGE: `Please provide the following information to add a new miscellaneous charge (trip charge, diagnostic fee, etc.) to this ${objectName.toLowerCase()}`,
            EDIT_OTHER_CHARGE: "Use the fields below to edit this miscellaneous charge",
            ADD_DISCOUNT: `Please provide the following information to add a discount to this ${objectName.toLowerCase()}:`,
            EDIT_DISCOUNT: "Use the fields below to edit this discount:",
        }

        return FORM_MODE_CAPTIONS[mode]
    }

    getQuickBooksItemOptions = () => {
        const { mode } = this.props
        const itemType = LINE_ITEM_TYPE_MODE_MAP[mode]

        return window.QUICKBOOKS_DESKTOP_ITEMS.filter(item => item.item_type === itemType || item.item_type === null)
    }

    shouldShowInventoryDepletionWarning = () => {
        const {
            parent,
            lineItem,
            isEstimateLineItem=false,
            isJobLineItem=false,
            isInvoiceLineItem=false,
            isPurchaseOrderLineItem=false,
        } = this.props

        let initialQuantity = 0
        let initialInventoryLevel = null
        let inventoryLevel = null

        if (valueIsDefined(parent.line_items) && parent.line_items.length !== 0) {
            const initialItem = parent.line_items.find(part => part.description === lineItem.description)
            if (valueIsDefined(initialItem)) {
                initialQuantity = parseFloat(initialItem.quantity)

                if (valueIsDefined(initialItem.inventory_location) && valueIsDefined(this.state.inventoryLevels)) {
                    initialInventoryLevel = this.state.inventoryLevels.find(level => level.location === parseInt(initialItem.inventory_location))
                }
            }
        }

        if (valueIsDefined(lineItem.inventory_location) && valueIsDefined(this.state.inventoryLevels)) {
            inventoryLevel = this.state.inventoryLevels.find(level => level.location === parseInt(lineItem.inventory_location))
        }

        return (
            lineItem.line_item_type === PriceBookItemTypes.part &&  // This is a part
            lineItem.quantity > 0 &&  // A quantity is specified
            this.itemIsInventoryPart(this.state.selectedPriceBookItem) &&  // This is an inventory part
            inventoryLevel !== null &&  // We have inventory level data
            ((isJobLineItem && !!!parent.is_job_walk) || isInvoiceLineItem) &&  // This is either a service job or an invoice
            (parseFloat(lineItem.quantity) > initialQuantity || (initialInventoryLevel?.id !== inventoryLevel.id)) && // The quantity was increased since during edit, or the location changed
            (
                (initialInventoryLevel?.id === inventoryLevel.id)
                ?
                (parseFloat(inventoryLevel.quantity_on_hand) - (parseFloat(lineItem.quantity) - initialQuantity)) < 0  // Increasing the initial quantity would bring quantity on hand below 0
                :
                (parseFloat(inventoryLevel.quantity_on_hand) - parseFloat(lineItem.quantity)) < 0  // The new quantity would bring quantity on hand below 0
            )
        )
    }

    renderButtons = () => {
        const {
            mode,
            submitting,
            parent,
            lineItem,
            currencySymbol,
            errors,
            onFormDataChange,
            requestAction,
            switchToPrimaryForm,
            returnScroll,
            objectName,
            isEstimateLineItem=false,
            isJobLineItem=false,
            isFinalizedJob=false,
            isInvoiceLineItem=false,
            isPurchaseOrderLineItem=false,
            isStockedPurchaseOrder=false,
            showQuickBooksItemSelect=false,
            useTaxes=false,
            pricebookDefaultTaxableService=false,
            pricebookDefaultTaxablePart=false,
            pricebookDefaultTaxableOther=false,
            estimatedDuration=null,
            totalTimeLogged=null,
        } = this.props

        if (errors.unexpectedError) {
            return (
                <div className="data-panel__action-feedback">
                    <span className="text-invalid"><strong>An unexpected error occurred.</strong></span>
                </div>
            )
        }
        else {
            if (submitting) {
                return <Spinner centered={true} />
            }
            else {
                if (mode.startsWith("ADD_")) {
                    return (
                        <ButtonGroup>
                            <ButtonGroupRow>
                                {lineItem.description && <UniversalButton type="primary" text="Add" handler={event => this.handleRequestAction(mode)} />}
                                <UniversalButton type="secondary" text="Cancel" handler={event => switchToPrimaryForm()} />
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
                else {
                    return (
                        <ButtonGroup>
                            {
                                lineItem.description && (
                                    <ButtonGroupRow>
                                        <UniversalButton type="primary" text="Save" handler={event => this.handleRequestAction(mode)} />
                                    </ButtonGroupRow>
                                )
                            }
                            <ButtonGroupRow>
                                {lineItem.description && !(isStockedPurchaseOrder && isPurchaseOrderLineItem) && !(isFinalizedJob && isJobLineItem) && <UniversalButton type="danger" text="Delete" handler={event => requestAction("DELETE_" + mode.replace("ADD_", "").replace("EDIT_", ""))} />}
                                <UniversalButton type="secondary" text="Cancel" handler={event => switchToPrimaryForm()} />
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
            }
        }
    }

    handleSelectionChange = (selectedOption) => {
        const {
            mode,
            submitting,
            parent,
            lineItem,
            priceBookClientOverrides=[],
            currencySymbol,
            errors,
            onFormDataChange,
            requestAction,
            switchToPrimaryForm,
            returnScroll,
            objectName,
            isEstimateLineItem=false,
            isJobLineItem=false,
            isInvoiceLineItem=false,
            isPurchaseOrderLineItem=false,
            isStockedPurchaseOrder=false,
            isBillLineItem=false,
            isCreditNoteLineItem=false,
            showQuickBooksItemSelect=false,
            useTaxes=false,
            pricebookDefaultTaxableService=false,
            pricebookDefaultTaxablePart=false,
            pricebookDefaultTaxableOther=false,
            parentLineItems=[],
            parentLineItemFieldName="",
            parentLineItemLabel="",
            estimatedDuration=null,
            totalTimeLogged=null,
        } = this.props

        if (selectedOption !== null) {
            if (!selectedOption.hasOwnProperty("object")) {
                onFormDataChange("description", selectedOption.value || null)

                // A custom-typed description has no pricebook item and so no catalog link to source from, so any
                // vendor and staged product options from the previous selection are dropped.
                if (this.isSalesLineItem()) {
                    onFormDataChange("ecommerce_vendor", null)
                    onFormDataChange("platform_options", [])
                }

                this.setState({inventoryLevels: null, lastPurchasedDate: undefined, selectedPriceBookItem: null, selectedSubtype: null, selectedItemCatalogLinks: []})
            }
            else {
                const priceBookItem = selectedOption.object
                const matchingOverride = priceBookClientOverrides.find((override) => override.pricebook_item.id === priceBookItem.id)

                if (priceBookItem.description !== lineItem.description) {
                    onFormDataChange("description", priceBookItem.description)
                    onFormDataChange("unit_type", priceBookItem.default_unit_type)
                    if (isBillLineItem) {
                        onFormDataChange("price", parseFloat(priceBookItem.cost))
                    }
                    else {
                        onFormDataChange("cost", parseFloat(priceBookItem.cost))
                        onFormDataChange("price", matchingOverride? parseFloat(matchingOverride.price) : parseFloat(priceBookItem.default_price))
                    }
                    onFormDataChange("summary", priceBookItem.summary_template)
                    onFormDataChange("is_taxable", priceBookItem.default_is_taxable)
                    onFormDataChange("quickbooks_desktop_item_id", priceBookItem.quickbooks_desktop_id)

                    // Set default subtype if available
                    if (valueIsDefined(priceBookItem.subtype?.id)) {
                        const selectedSubtype = this.state.subtypeChoices.find(subtype => subtype.value === priceBookItem.subtype.id);
                        if (selectedSubtype) {
                            onFormDataChange("subtype", selectedSubtype.value);
                            onFormDataChange("subtype_label", selectedSubtype.label);
                            this.setState({ selectedSubtype });
                        }
                        priceBookItem.subtype = priceBookItem.subtype.id;
                        priceBookItem.subtype_label = selectedSubtype.label;
                    } else {
                        onFormDataChange("subtype", null)
                        onFormDataChange("subtype_label", "")
                        this.setState({selectedSubtype: null})
                    }

                    if (priceBookItem.pricebook_item_type === PriceBookItemTypes.part && (isPurchaseOrderLineItem || isBillLineItem) && valueIsDefined(parent.vendor)) {
                        const vendorPartCost = priceBookItem.vendor_part_costs.find(vendorCost => vendorCost.vendor === parent.vendor)

                        if (vendorPartCost) {
                            if (isPurchaseOrderLineItem) {
                                onFormDataChange("expected_cost", vendorPartCost.vendor_cost)
                                onFormDataChange("part_number", vendorPartCost.vendor_part_number)
                            }
                            else {
                                onFormDataChange("price", vendorPartCost.vendor_cost)
                            }
                        }
                        else {
                            if (isPurchaseOrderLineItem) {
                                onFormDataChange("expected_cost", "")
                                onFormDataChange("part_number", "")
                            }
                        }
                    }

                    if (this.itemIsInventoryPart(priceBookItem)) {
                        this.fetchInventoryLevels(priceBookItem.id)
                    }

                    if (isPurchaseOrderLineItem && valueIsDefined(parent.vendor)) {
                        this.setState({lastPurchasedDate: undefined})
                        const hasPurchaseOrdersEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_purchase_orders_enabled;
                        if (hasPurchaseOrdersEntitlement) {
                            this.fetchLastPurchasedDate(priceBookItem.id, parent.vendor)
                        }
                    }

                    if (!priceBookItem.track_inventory) {
                        onFormDataChange("inventory_location", null)
                        onFormDataChange("inventory_location_label", "")
                    }
                }

                // The detail payload carries the catalog links; a part with no connected link cannot be sourced
                // from an e-commerce vendor, so any vendor carried over from the previous selection is cleared.
                // Switching between two connected-catalog parts keeps the vendor untouched.
                const nextCatalogLinks = priceBookItem.catalog_links || []
                const nextHasConnectedCatalogLink = nextCatalogLinks.some(catalogLink => catalogLink.is_connected)
                if (this.isSalesLineItem() && !nextHasConnectedCatalogLink) {
                    onFormDataChange("ecommerce_vendor", null)
                    onFormDataChange("platform_options", [])
                }

                this.setState({selectedPriceBookItem: priceBookItem, selectedItemCatalogLinks: nextCatalogLinks}, this.maybeAutoSelectEcommerceVendor)
            }
        }
        else {
            onFormDataChange("description", "")
            onFormDataChange("unit_type", "")
            onFormDataChange("quantity", "")
            onFormDataChange("cost", "")
            onFormDataChange("price", "")
            onFormDataChange("summary", "")
            onFormDataChange("is_taxable", "")
            onFormDataChange("quickbooks_desktop_item_id", "")
            onFormDataChange("expected_cost", "")
            onFormDataChange("part_number", "")
            onFormDataChange("subtype", null)
            onFormDataChange("ecommerce_vendor", null)
            onFormDataChange("platform_options", [])

            this.setState({inventoryLevels: null, lastPurchasedDate: undefined, selectedPriceBookItem: null, selectedSubtype: null, selectedItemCatalogLinks: []})
        }
    }

    render() {
        const {
            mode,
            submitting,
            parent,
            lineItem,
            priceBookClientOverrides=[],
            formatCurrencyValue,
            currencySymbol,
            errors,
            onFormDataChange,
            requestAction,
            switchToPrimaryForm,
            returnScroll,
            objectName,
            isEstimateLineItem=false,
            isJobLineItem=false,
            isFinalizedJob=false,
            isInvoiceLineItem=false,
            isPurchaseOrderLineItem=false,
            isStockedPurchaseOrder=false,
            isBillLineItem=false,
            isCreditNoteLineItem=false,
            showQuickBooksItemSelect=false,
            useTaxes=false,
            pricebookDefaultTaxableService=false,
            pricebookDefaultTaxablePart=false,
            pricebookDefaultTaxableOther=false,
            parentLineItems=[],
            parentLineItemFieldName="",
            parentLineItemLabel="",
            estimatedDuration=null,
            totalTimeLogged=null,
            selectedEquipment=[],
        } = this.props

        const matchingOverride = priceBookClientOverrides.find((override) => override.pricebook_item.id === this.state.selectedPriceBookItem?.id)
        const hasJobsEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_jobs_enabled;

        const equipmentChoices = selectedEquipment
            .filter(equipment => equipment && typeof equipment === "object" && valueIsDefined(equipment.display_name))
            .map(equipment => ({ value: equipment.id, label: equipment.display_name }))
            .sort((choiceA, choiceB) => choiceA.label.localeCompare(choiceB.label))

        return (
            <div className="data-panel-container data-panel-container--with-margin">
                <div className="data-panel" aria-label="Line Item Create/Update">
                    <div className="data-panel__form" aria-label="Line Item Create/Update Form">
                        <p className="data-panel__form__caption">{this.getFormCaption(mode, objectName)}</p>
                            {lineItem.description && this.state.selectedPriceBookItem === null  && (
                                    <Banner
                                        type="info"
                                        text={"This line item does not exist in your standard pricebook. Custom items may lack verified pricing, costs, and detailed specifications."}
                                    />
                                )}
                            <SearchCreateSelectField
                                fieldName="description"
                                fieldLabel="Short Description"
                                fieldValue={lineItem.description || null}
                                inputComponent={
                                    this.state.selectedPriceBookItem === undefined
                                    ?
                                    <Spinner />
                                    :
                                    <PureComponentWrapper
                                        wrappedComponent={PriceBookItemSearchOrCreateSelect}
                                        updateTrigger={(currentProps, nextProps) => nextProps.defaultSelected?.value !== currentProps.defaultSelected?.value}
                                        onSelectionChange={selectedOption => this.handleSelectionChange(selectedOption)}
                                        defaultSelected={this.state.selectedPriceBookItem ? priceBookItemToPriceBookItemOption(this.state.selectedPriceBookItem) : (lineItem.description ? {value: null, label: lineItem.description} : null)}
                                        pricebookItemType={LINE_ITEM_TYPE_MODE_MAP[mode]}
                                        showCreateButton={false}
                                        creatable={!isPurchaseOrderLineItem && window.CURRENT_USER?.permissions.pricebook_create_permission >= PERMISSION_LEVEL.RESTRICTED}
                                        disabled={
                                            (isStockedPurchaseOrder && isPurchaseOrderLineItem) ||
                                            (isFinalizedJob && isJobLineItem) ||
                                            window.CURRENT_USER?.permissions.pricebook_list_permission < PERMISSION_LEVEL.RESTRICTED ||
                                            window.CURRENT_USER?.permissions.pricebook_view_permission < PERMISSION_LEVEL.FULL
                                        }
                                    ></PureComponentWrapper>
                                }
                                showButton={this.state.selectedPriceBookItem && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                                buttonLabel={<Fragment>Edit Pricebook Item{"  "}<Icon containerClassName="icon-inline" icon={faIcon({ name: "arrow-up-right-from-square", style: "regular" })} /></Fragment>}
                                buttonAction={() => { window.open(DjangoUrls["pricebook:pricebookitem-update"]( window.MARKETPLACE_ENTITY_SLUG, this.state.selectedPriceBookItem?.id ), "_blank") }}
                                errors={errors}
                            ></SearchCreateSelectField>
                            {
                                lineItem.line_item_type === PriceBookItemTypes.part && (
                                    <PriceBookPartDataDisplay lineItem={lineItem} errors={errors} />
                                )
                            }
                            {window.CURRENT_USER?.permissions.pricebook_view_permission >= PERMISSION_LEVEL.FULL &&
                                lineItem.description && this.state.lastPurchasedDate === null
                                ?
                                    <Banner type="warning" text="The selected part has not been purchased from this vendor before. We recommend checking the vendor cost to confirm." extraMargin={true} />
                                :
                                this.shouldShowVendorCostWarning() && (
                                    <AccessCheck entitlements={["entitlement_purchase_orders_enabled"]}>
                                        <Banner type="warning" text="The selected part was last purchased from this vendor over a month ago. We recommend checking the vendor cost to confirm." extraMargin={true} />
                                    </AccessCheck>
                                )
                            }
                            {
                                // Only show subtype if there are subtypes available
                                lineItem.description && this.state.subtypeChoices.length > 0 && (
                                    <>
                                    <BasicSelectField
                                        key={`subtype_for_${this.state.selectedPriceBookItem?.id ?? "new_item"}`}
                                        fieldName="subtype"
                                        fieldLabel="Subtype"
                                        fieldValue={lineItem.subtype || ""}
                                        fieldOnChange={subtype => {
                                            onFormDataChange("subtype", subtype || "")

                                            if (subtype) {
                                                onFormDataChange("subtype_label", this.state.subtypeChoices.find(location => location.value === parseInt(subtype)).label)
                                            }
                                        }}
                                        choices={[
                                            { value: "", label: "Choose a subtype..." },
                                            ...this.state.subtypeChoices
                                        ]}
                                        disabled={window.CURRENT_USER?.permissions.cannot_classify_subtypes}
                                        errors={errors}
                                    ></BasicSelectField>
                                    {valueIsDefined(this.state.selectedPriceBookItem?.subtype) &&
                                        parseInt(lineItem.subtype, 10) !== this.state.selectedPriceBookItem?.subtype && (
                                            <>
                                                <Banner
                                                    type="info"
                                                    text={
                                                        <>
                                                        The subtype of this charge does not match its subtype in the pricebook.
                                                        <br />
                                                        {
                                                        window.CURRENT_USER?.permissions.pricebook_edit_permission ===
                                                            PERMISSION_LEVEL.FULL && (
                                                            <span>
                                                                <a
                                                                    className="text-link text-link--inline"
                                                                    href={DjangoUrls["pricebook:pricebookitem-update"](
                                                                        window.MARKETPLACE_ENTITY_SLUG,
                                                                        this.state.selectedPriceBookItem?.id,
                                                                    )}
                                                                    target="_blank"
                                                                >
                                                                    Click here
                                                                </a>{" "}
                                                                to update the pricebook.
                                                            </span>
                                                            )
                                                        }
                                                        </>
                                                    }
                                                />
                                                <div style={{ marginBottom: "1em" }}></div>
                                            </>
                                        )}
                                    </>
                                )
                            }
                        {
                            lineItem.description && <Fragment key={lineItem.description}>
                                {
                                    parentLineItems.length > 0 && parentLineItemFieldName && (
                                        <BasicSelectField
                                            fieldName={parentLineItemFieldName}
                                            fieldLabel={parentLineItemLabel}
                                            fieldValue={lineItem[parentLineItemFieldName] || ""}
                                            fieldOnChange={value => onFormDataChange(parentLineItemFieldName, value || "")}
                                            choices={[
                                                { value: "", label: `Select ${parentLineItemLabel.toLowerCase()}...` },
                                                ...parentLineItems.map(item => ({ value: item.id, label: item.__str__ }))
                                            ]}
                                            optional={true}
                                            errors={errors}
                                        ></BasicSelectField>
                                    )
                                }
                                {
                                    mode.includes("_SERVICE") && !isEstimateLineItem && (isJobLineItem ? !parent.is_job_walk : true) && !isBillLineItem && !isCreditNoteLineItem && (
                                        <Fragment>
                                             <HorizontalLayout>
                                                <DateField
                                                    fieldName="service_start_date"
                                                    fieldLabel="Start Date"
                                                    fieldValue={lineItem.service_start_date}
                                                    fieldOnChange={service_start_date => onFormDataChange("service_start_date", service_start_date)}
                                                    disabled={isFinalizedJob && isJobLineItem}
                                                    errors={errors}
                                                ></DateField>
                                                <DateField
                                                    fieldName="service_end_date"
                                                    fieldLabel="End Date"
                                                    fieldValue={lineItem.service_end_date}
                                                    fieldOnChange={service_end_date => onFormDataChange("service_end_date", service_end_date)}
                                                    disabled={isFinalizedJob && isJobLineItem}
                                                    errors={errors}
                                                ></DateField>
                                            </HorizontalLayout>
                                        </Fragment>
                                    )
                                }
                                {
                                    mode.includes("_SERVICE") && (
                                        <SwitchField
                                            key={`unit_type_${this.state.selectedPriceBookItem?.id}`}
                                            fieldName="unit_type"
                                            fieldLabel="Unit Type"
                                            fieldValue={lineItem.unit_type !== undefined ? lineItem.unit_type === LineItemUnitTypes.flat_rate : false}
                                            fieldOnChange={unit_type => onFormDataChange("unit_type", unit_type ? LineItemUnitTypes.flat_rate : LineItemUnitTypes.hourly)}
                                            uncheckedText="Hourly"
                                            checkedText="Flat Rate"
                                            disabled={isFinalizedJob && isJobLineItem}
                                            errors={errors}
                                        ></SwitchField>
                                    )
                                }
                                {
                                    mode.includes("_SERVICE") && lineItem.unit_type !== LineItemUnitTypes.flat_rate && (
                                        <HorizontalLayout>
                                            {
                                                estimatedDuration !== null && (
                                                    <BasicDisplayField
                                                        fieldName="duration"
                                                        fieldLabel="Est. Job Duration"
                                                        fieldValue={formatDuration(estimatedDuration)}
                                                    ></BasicDisplayField>
                                                )
                                            }
                                            {
                                                totalTimeLogged !== null && (
                                                    <BasicDisplayField
                                                        fieldName="total_time_logged"
                                                        fieldLabel="Total Time Logged"
                                                        fieldValue={formatDuration(totalTimeLogged)}
                                                    ></BasicDisplayField>
                                                )
                                            }
                                        </HorizontalLayout>
                                    )
                                }
                                {
                                    (!mode.includes("_SERVICE") || lineItem.unit_type !== LineItemUnitTypes.flat_rate) ?
                                    (
                                        <Fragment>
                                            {
                                                lineItem.line_item_type === PriceBookItemTypes.part && (isJobLineItem || isInvoiceLineItem || isPurchaseOrderLineItem || isCreditNoteLineItem) && this.itemIsInventoryPart(this.state.selectedPriceBookItem) && (
                                                    <BasicSelectField
                                                        fieldName="inventory_location"
                                                        fieldLabel="Inventory Location"
                                                        fieldValue={lineItem.inventory_location || ""}
                                                        fieldOnChange={inventory_location => {
                                                            onFormDataChange("inventory_location", inventory_location || "")

                                                            if (inventory_location) {
                                                                onFormDataChange("inventory_location_label", window.INVENTORY_LOCATION_CHOICES.find(location => location.value === parseInt(inventory_location)).label)
                                                            }
                                                        }}
                                                        choices={filterInventoryLocationChoices(window.INVENTORY_LOCATION_CHOICES, lineItem.inventory_location)}
                                                        disabled={(isStockedPurchaseOrder && isPurchaseOrderLineItem) || (isFinalizedJob && isJobLineItem)}
                                                        errors={errors}
                                                    ></BasicSelectField>
                                                )
                                            }
                                            {
                                                this.renderInventoryLocationChangedWarning(lineItem, isCreditNoteLineItem, parentLineItems)
                                            }
                                            {
                                                isCreditNoteLineItem && lineItem.line_item_type === PriceBookItemTypes.part && valueIsDefined(lineItem.invoice_line_item) && this.itemIsInventoryPart(this.state.selectedPriceBookItem) && (
                                                    <BooleanField
                                                        fieldName="return_to_inventory"
                                                        fieldLabel="Return to Inventory"
                                                        fieldValue={lineItem.return_to_inventory || false}
                                                        fieldOnChange={return_to_inventory => onFormDataChange("return_to_inventory", return_to_inventory)}
                                                        inputLabel="Return this part to inventory"
                                                        errors={errors}
                                                    ></BooleanField>
                                                )
                                            }
                                            {
                                                isPurchaseOrderLineItem
                                                ?
                                                    <Fragment>
                                                        <fieldset>
                                                            <legend>Purchasing Options</legend>
                                                            <HorizontalLayout>
                                                                <QuantityField
                                                                    fieldName="quantity"
                                                                    fieldLabel="Quantity"
                                                                    fieldValue={lineItem.quantity || ""}
                                                                    fieldOnChange={quantity => onFormDataChange("quantity", quantity || "")}
                                                                    disabled={isStockedPurchaseOrder}
                                                                    errors={errors}
                                                                ></QuantityField>
                                                                <PriceField
                                                                    key={`expected_cost_${this.state.selectedPriceBookItem?.id}`}
                                                                    fieldName="expected_cost"
                                                                    fieldLabel="Expected Vendor Cost"
                                                                    fieldValue={lineItem.expected_cost}
                                                                    fieldOnChange={expected_cost => onFormDataChange("expected_cost", expected_cost)}
                                                                    currencySymbol={currencySymbol}
                                                                    errors={errors}
                                                                ></PriceField>
                                                            </HorizontalLayout>
                                                            <CharField
                                                                key={`part_number_${this.state.selectedPriceBookItem?.id}`}
                                                                fieldName="part_number"
                                                                fieldLabel="Vendor Part #"
                                                                fieldValue={lineItem.part_number || ""}
                                                                fieldOnChange={part_number => onFormDataChange("part_number", part_number || "")}
                                                                maxLength={100}
                                                                errors={errors}
                                                            ></CharField>
                                                            {this.renderSummaryField()}
                                                        </fieldset>
                                                        {this.renderCatalogModifiers()}
                                                    </Fragment>
                                                :
                                                    <>
                                                    {
                                                        (mode.includes("_PART") || mode.includes("_CHARGE")) && !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && !isBillLineItem && !isCreditNoteLineItem && (
                                                            <>
                                                                <PriceField
                                                                    fieldName="cost"
                                                                    fieldLabel={`${(lineItem.line_item_type === PriceBookItemTypes.service && lineItem.unit_type === LineItemUnitTypes.hourly) ? "Hourly Cost" : "Unit Cost"}`}
                                                                    fieldValue={lineItem.cost}
                                                                    fieldOnChange={cost => onFormDataChange("cost", cost)}
                                                                    currencySymbol={currencySymbol}
                                                                    optional={isJobLineItem}
                                                                    disabled={isFinalizedJob && isJobLineItem}
                                                                    errors={errors}
                                                                ></PriceField>
                                                                {
                                                                    valueIsDefined(lineItem.cost) && valueIsDefined(this.state.selectedPriceBookItem?.cost) && parseFloat(lineItem.cost) !== parseFloat(this.state.selectedPriceBookItem?.cost) && (
                                                                        <>
                                                                            <Banner
                                                                                type="info"
                                                                                text={
                                                                                    <>
                                                                                        The cost of this charge does not match its cost in the pricebook.
                                                                                        <br />
                                                                                        <span><a className="text-link text-link--inline" href={DjangoUrls["pricebook:pricebookitem-update"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedPriceBookItem?.id)} target="_blank">Click here</a> to update the pricebook.</span>
                                                                                    </>
                                                                                }
                                                                            />
                                                                            <div style={{marginBottom: "1em"}}></div>
                                                                        </>
                                                                    )
                                                                }
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        this.shouldShowEcommerceVendorSelect() && (
                                                            <BasicSelectField
                                                                key={`ecommerce_vendor_${this.state.selectedPriceBookItem?.id ?? "new_item"}_${lineItem.ecommerce_vendor ?? "none"}`}
                                                                fieldName="ecommerce_vendor"
                                                                fieldLabel="Ecommerce Vendor"
                                                                fieldValue={lineItem.ecommerce_vendor || ""}
                                                                fieldOnChange={ecommerceVendor => this.handleEcommerceVendorChange(ecommerceVendor || null)}
                                                                choices={[
                                                                    { value: "", label: "Choose an ecommerce vendor..." },
                                                                    ...this.state.ecommerceVendors.map(vendor => ({ value: vendor.id, label: vendor.name }))
                                                                ]}
                                                                optional={true}
                                                                errors={errors}
                                                            ></BasicSelectField>
                                                        )
                                                    }
                                                    {this.renderCatalogModifiers()}
                                                    <HorizontalLayout>
                                                        <QuantityField
                                                            fieldName="quantity"
                                                            fieldLabel={<Fragment>{(mode.includes("_SERVICE_CHARGE") && (lineItem.unit_type === LineItemUnitTypes.hourly || lineItem.unit_type === undefined)) ? "Hours" : "Quantity"}{isJobLineItem ? <Fragment><br /><br /></Fragment> : ""}</Fragment>}
                                                            fieldValue={lineItem.quantity || ""}
                                                            fieldOnChange={quantity => onFormDataChange("quantity", quantity || "")}
                                                            disabled={isFinalizedJob && isJobLineItem}
                                                            errors={errors}
                                                        ></QuantityField>
                                                        {
                                                            !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && (
                                                                <PriceField
                                                                    key={`price_${this.state.selectedPriceBookItem?.id}`}
                                                                    fieldName="price"
                                                                    fieldLabel={<Fragment>{(mode.includes("_SERVICE_CHARGE") && (lineItem.unit_type === LineItemUnitTypes.hourly || lineItem.unit_type === undefined)) ? (isBillLineItem? "Hourly Cost" : "Hourly Rate") : (isBillLineItem ? "Unit Cost" : "Unit Price")}{isJobLineItem ? <br /> : ""}</Fragment>}
                                                                    fieldValue={lineItem.price}
                                                                    fieldOnChange={price => onFormDataChange("price", price)}
                                                                    currencySymbol={currencySymbol}
                                                                    credit={mode.includes("_DISCOUNT")}
                                                                    errors={errors}
                                                                    optional={isJobLineItem}
                                                                    disabled={isFinalizedJob && isJobLineItem}
                                                                ></PriceField>
                                                            )
                                                        }
                                                    </HorizontalLayout>
                                                    {
                                                        !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && valueIsDefined(matchingOverride) && window.CURRENT_USER?.permissions.pricebook_view_permission >= PERMISSION_LEVEL.FULL && (
                                                            <>
                                                                <Banner
                                                                    type="info"
                                                                    text={
                                                                        <>
                                                                            Special price for this client:
                                                                            <br />
                                                                            <span>{formatCurrencyValue(lineItem.line_item_type === PriceBookItemTypes.discount ? matchingOverride.pricebook_item.default_price * -1 : matchingOverride.pricebook_item.default_price)}{" -> "}{formatCurrencyValue(lineItem.line_item_type === PriceBookItemTypes.discount ? matchingOverride.price * -1 : matchingOverride.price)}</span>
                                                                        </>
                                                                    }
                                                                />
                                                                <div style={{marginBottom: "1em"}}></div>
                                                            </>
                                                        )
                                                    }
                                                    </>
                                            }
                                            {
                                                this.shouldShowInventoryDepletionWarning() && (
                                                    <Banner type="warning" text="Using this quantity for this part would cause its inventory level to drop below 0. Please confirm you've selected the correct part." />
                                                )
                                            }
                                        </Fragment>
                                    )
                                    :
                                    !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && (
                                            <Fragment>
                                                {
                                                    (mode.includes("_PART") || mode.includes("_CHARGE")) && !isBillLineItem && !isCreditNoteLineItem && (
                                                        <>
                                                            <PriceField
                                                                fieldName="cost"
                                                                fieldLabel={`${(lineItem.line_item_type === PriceBookItemTypes.service && lineItem.unit_type === LineItemUnitTypes.hourly) ? "Hourly Cost" : "Unit Cost"}`}
                                                                fieldValue={lineItem.cost}
                                                                fieldOnChange={cost => onFormDataChange("cost", cost)}
                                                                currencySymbol={currencySymbol}
                                                                optional={isJobLineItem}
                                                                disabled={isFinalizedJob && isJobLineItem}
                                                                errors={errors}
                                                            ></PriceField>
                                                            {
                                                                valueIsDefined(lineItem.cost) && valueIsDefined(this.state.selectedPriceBookItem?.cost) && parseFloat(lineItem.cost) !== parseFloat(this.state.selectedPriceBookItem?.cost) && (
                                                                    <>
                                                                        <Banner
                                                                            type="info"
                                                                            text={
                                                                                <>
                                                                                    The cost of this charge does not match its cost in the pricebook.
                                                                                    <br />
                                                                                    <span><a className="text-link text-link--inline" href={DjangoUrls["pricebook:pricebookitem-update"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedPriceBookItem?.id)} target="_blank">Click here</a> to update the pricebook.</span>
                                                                                </>
                                                                            }
                                                                        />
                                                                        <div style={{marginBottom: "1em"}}></div>
                                                                    </>
                                                                )
                                                            }
                                                        </>
                                                    )
                                                }

                                                <PriceField
                                                    key={`price_${this.state.selectedPriceBookItem?.id}`}
                                                    fieldName="price"
                                                    fieldLabel={<Fragment>{"Rate"}{isJobLineItem ? <br /> : ""}</Fragment>}
                                                    fieldValue={lineItem.price}
                                                    fieldOnChange={price => onFormDataChange("price", price)}
                                                    currencySymbol={currencySymbol}
                                                    credit={mode.includes("_DISCOUNT")}
                                                    errors={errors}
                                                    optional={isJobLineItem}
                                                    disabled={isFinalizedJob && isJobLineItem}
                                                ></PriceField>
                                                {
                                                    valueIsDefined(matchingOverride) && window.CURRENT_USER?.permissions.pricebook_view_permission >= PERMISSION_LEVEL.FULL && (
                                                        <>
                                                            <Banner
                                                                type="info"
                                                                text={
                                                                    <>
                                                                        Special price for this client:
                                                                        <br />
                                                                        <span>{formatCurrencyValue(matchingOverride.pricebook_item.default_price)}{" -> "}{formatCurrencyValue(matchingOverride.price)}</span>
                                                                    </>
                                                                }
                                                            />
                                                            <div style={{marginBottom: "1em"}}></div>
                                                        </>
                                                    )
                                                }
                                            </Fragment>
                                        )
                                }
                                {
                                    useTaxes && (isEstimateLineItem || isInvoiceLineItem || isCreditNoteLineItem) && !mode.includes("_DISCOUNT") && (
                                        <SwitchField
                                            key={`is_taxable_${this.state.selectedPriceBookItem?.id}`}
                                            fieldName="is_taxable"
                                            fieldLabel="Tax"
                                            fieldValue={valueIsDefined(lineItem.is_taxable) ? lineItem.is_taxable : getIsTaxable(lineItem, pricebookDefaultTaxableService, pricebookDefaultTaxablePart, pricebookDefaultTaxableOther)}
                                            fieldOnChange={is_taxable => onFormDataChange("is_taxable", is_taxable)}
                                            uncheckedText="Non-Taxable"
                                            checkedText="Taxable"
                                            disabled={isFinalizedJob && isJobLineItem}
                                            errors={errors}
                                        ></SwitchField>
                                    )
                                }
                                {!isPurchaseOrderLineItem && this.renderSummaryField()}
                                {
                                    (isEstimateLineItem || isInvoiceLineItem || isJobLineItem) && equipmentChoices.length !== 0 && (
                                        <BasicSelectField
                                            key={`equipment_select_${lineItem.description}`}
                                            fieldName="equipment"
                                            fieldLabel="Equipment Attribution"
                                            fieldValue={lineItem.equipment || ""}
                                            fieldOnChange={equipmentID => {
                                                onFormDataChange("equipment", equipmentID || "")
                                                if (equipmentID) {
                                                    const selectedChoice = equipmentChoices.find(choice => String(choice.value) === String(equipmentID))
                                                    onFormDataChange("equipment_display_name", selectedChoice?.label || "")
                                                } else {
                                                    onFormDataChange("equipment_display_name", "")
                                                }
                                            }}
                                            choices={[{ value: "", label: "Choose equipment..." }, ...equipmentChoices]}
                                            optional={true}
                                            errors={errors}
                                        ></BasicSelectField>
                                    )
                                }
                                {
                                    (() => {
                                        // Build technician choices based on context
                                        let technicianChoices = []
                                        if ((isJobLineItem || isInvoiceLineItem) && Array.isArray(window.WORKING_TECHNICIANS)) {
                                            // Build set of allowed technician IDs: assigned technicians + currently attributed
                                            const allowedIds = new Set()
                                            if (isJobLineItem && Array.isArray(parent.assigned_technicians)) {
                                                parent.assigned_technicians.forEach(user => {
                                                    if (!user) return
                                                    const uid = valueIsDefined(user.hashid) ? String(user.hashid) : (valueIsDefined(user.id) ? String(user.id) : null)
                                                    if (uid) allowedIds.add(uid)
                                                })
                                            }
                                            // Always include currently attributed technician
                                            if (lineItem.user) {
                                                allowedIds.add(String(lineItem.user))
                                            }

                                            // For invoices, show all working technicians; for jobs, filter to allowed
                                            technicianChoices = window.WORKING_TECHNICIANS
                                                .filter(opt => isInvoiceLineItem || allowedIds.has(String(opt?.value)))
                                                .map(opt => ({
                                                    value: String(opt.value),
                                                    label: opt.label
                                                }))
                                        }
                                        return (technicianChoices.length !== 0) && (
                                            <BasicSelectField
                                                key={`technician_select_${lineItem.description}`}
                                                fieldName="user"
                                                fieldLabel="Technician Attribution"
                                                fieldValue={lineItem.user || ""}
                                                fieldOnChange={userID => {
                                                    onFormDataChange("user", userID || "")
                                                    if (userID) {
                                                        const selectedOpt = window.WORKING_TECHNICIANS?.find(opt => String(opt?.value) === String(userID))
                                                        onFormDataChange("user_display_name", selectedOpt?.label || "")
                                                    } else {
                                                        onFormDataChange("user_display_name", "")
                                                    }
                                                }}
                                                choices={[{ value: "", label: "Choose technician..." }, ...technicianChoices]}
                                                optional={true}
                                                errors={errors}
                                            ></BasicSelectField>
                                        )
                                    })()
                                }
                                {
                                    (isEstimateLineItem || isInvoiceLineItem) && showQuickBooksItemSelect && (
                                        <BasicSelectField
                                            key={`qb_select_${this.state.selectedPriceBookItem?.id}`}
                                            fieldName="quickbooks_desktop_item_id"
                                            fieldLabel="QuickBooks PriceBook Item"
                                            fieldValue={lineItem.quickbooks_desktop_item_id || ""}
                                            fieldOnChange={quickbooks_desktop_item_id => onFormDataChange("quickbooks_desktop_item_id", quickbooks_desktop_item_id || "")}
                                            choices={this.getQuickBooksItemOptions()}
                                            errors={errors}
                                            optional={isEstimateLineItem}
                                        ></BasicSelectField>
                                    )
                                }
                                {
                                    isPurchaseOrderLineItem && (
                                    <fieldset>
                                        <legend>Associations</legend>
                                        <AccessCheck entitlements={["entitlement_estimates_enabled"]}>
                                            <SearchCreateSelectField
                                                fieldName="estimates"
                                                fieldLabel="Associated Estimates"
                                                fieldValue={lineItem.estimates || []}
                                                inputComponent={
                                                    this.state.selectedEstimates === undefined
                                                    ?
                                                    <Spinner />
                                                    :
                                                    <EstimateSearchOrCreateSelect
                                                        isMulti={true}
                                                        refetchSelection={false}
                                                        onSelectionChange={selectedOptions => {
                                                            onFormDataChange("estimates", (selectedOptions || []).filter(option => option !== null).map(option => option.value))
                                                            onFormDataChange("estimates_label", (selectedOptions || []).filter(option => option !== null).map(option => option.object.custom_id || option.object.id).join(", "))
                                                            this.setState({selectedEstimates: (selectedOptions || [])})
                                                        }}
                                                        defaultSelected={this.state.selectedEstimates.map(estimate => estimateToEstimateOption(estimate, this.props.preferredTimezone))}
                                                        showCreateButton={false}
                                                        preferredTimezone={this.props.preferredTimezone}
                                                    ></EstimateSearchOrCreateSelect>
                                                }
                                                showButton={false}
                                                optional={true}
                                                errors={errors}
                                            ></SearchCreateSelectField>
                                        </AccessCheck>
                                        <AccessCheck entitlements={["entitlement_jobs_enabled"]}>
                                            <SearchCreateSelectField
                                                fieldName="jobs"
                                                fieldLabel="Associated Jobs"
                                                fieldValue={lineItem.jobs || []}
                                                inputComponent={
                                                    this.state.selectedJobs === undefined
                                                    ?
                                                    <Spinner />
                                                    :
                                                    <JobSearchOrCreateSelect
                                                        isMulti={true}
                                                        refetchSelection={false}
                                                        onSelectionChange={selectedOptions => {
                                                            onFormDataChange("jobs", (selectedOptions || []).filter(option => option !== null).map(option => option.value))
                                                            onFormDataChange("jobs_label", (selectedOptions || []).filter(option => option !== null).map(option => option.object.custom_id || option.object.id).join(", "))
                                                            this.setState({selectedJobs: (selectedOptions || [])})
                                                        }}
                                                        defaultSelected={this.state.selectedJobs.map(job => jobToJobOption(job, this.props.preferredTimezone))}
                                                        showCreateButton={false}
                                                        preferredTimezone={this.props.preferredTimezone}
                                                    ></JobSearchOrCreateSelect>
                                                }
                                                showButton={false}
                                                optional={true}
                                                errors={errors}
                                            ></SearchCreateSelectField>
                                        </AccessCheck>
                                        <AccessCheck entitlements={["entitlement_invoices_enabled"]}>
                                            <SearchCreateSelectField
                                                fieldName="invoices"
                                                fieldLabel="Associated Invoices"
                                                fieldValue={lineItem.invoices || []}
                                                inputComponent={
                                                    this.state.selectedInvoices === undefined
                                                    ?
                                                    <Spinner />
                                                    :
                                                    <InvoiceSearchOrCreateSelect
                                                        isMulti={true}
                                                        refetchSelection={false}
                                                        onSelectionChange={selectedOptions => {
                                                            onFormDataChange("invoices", (selectedOptions || []).filter(option => option !== null).map(option => option.value))
                                                            onFormDataChange("invoices_label", (selectedOptions || []).filter(option => option !== null).map(option => option.object.custom_id || option.object.id).join(", "))
                                                            this.setState({selectedInvoices: (selectedOptions || [])})
                                                        }}
                                                        defaultSelected={this.state.selectedInvoices.map(invoice => invoiceToInvoiceOption(invoice, this.props.preferredTimezone))}
                                                        showCreateButton={false}
                                                        preferredTimezone={this.props.preferredTimezone}
                                                    ></InvoiceSearchOrCreateSelect>
                                                }
                                                showButton={false}
                                                optional={true}
                                                errors={errors}
                                            ></SearchCreateSelectField>
                                        </AccessCheck>
                                    </fieldset>
                                    )
                                }
                            </Fragment>
                        }
                    </div>
                    {this.renderButtons()}
                </div>
            </div>
        )
    }
}

export default LineItemForm;
